import type React from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useTheme } from "@mui/material";
import { HTTPClient } from "../../HTTPClient";
import type { Gift } from "../../types/campaign/Gift";
import type { CampaignContextType } from "../../types/campaign/CampaignContext";
import { useSnackbar } from "../../providers/ToastProvider";
import { useTranslation } from "react-i18next";
import type { Action, Campaign, CampaignConfiguration } from "@tgc/types";
import { GiftType } from "@tgc/types";
import { isActionValid } from "../../utils/actions";
import { usePlace } from "../../providers/PlaceProvider";
import { ConditionField } from "../../types/campaign/Condition";
import type { PromoCode } from "../../types/campaign/PromoCode";

const CampaignContext = createContext<CampaignContextType | undefined>(undefined);

const CampaignProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation("", { keyPrefix: "views.campaign.save" });
  const { palette } = useTheme();
  const { snackbarShowMessage } = useSnackbar();
  const { selectedPlace } = usePlace();

  const [minPurchase, setMinPurchase] = useState<boolean>(false);
  const [minPurchaseAmount, setMinPurchaseAmount] = useState<string>('');
  const [conditionFields, setConditionFields] = useState<ConditionField[]>([]);
  const [skipCooldown, setSkipCooldown] = useState<boolean>(false);
  const [voucherExpiration, setVoucherExpiration] = useState<number | null>(null);
  const [voucherStart, setVoucherStart] = useState<number>(1);
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);

  const [id, setID] = useState<string>(selectedPlace?.id || "");
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [campaign, setCampaign] = useState<CampaignConfiguration | null>(null);
  const [gifts, setGifts] = useState<Gift[]>([]);
  const giftsRef = useRef<Gift[]>([]);
  useEffect(() => { giftsRef.current = gifts; }, [gifts]);
  const getGiftsForSaveRef = useRef<() => Gift[]>(() => giftsRef.current);
  const registerGetGiftsForSave = (fn: () => Gift[]) => { getGiftsForSaveRef.current = fn; };
  const [actions, setActions] = useState<Action[]>([]);
  const [game, setGame] = useState<string>("WHEEL");
  const [primaryColor, setPrimaryColor] = useState<string>(palette.primary.main);
  const [secondaryColor, setSecondaryColor] = useState<string>(palette.secondary.main);
  const [logo, setLogo] = useState<string>("");
  const [logoUri, setLogoUri] = useState<string>("");
  const [startDate, setStartDate] = useState<string | undefined>();
  const [label, setLabel] = useState<string>("");
  const [placeId, setPlaceId] = useState<string>("");
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [profile, setProfile] = useState<string>("PREMIUM");
  const [theme, setTheme] = useState<string>("DEFAULT");
  const [kioskMode, setKioskMode] = useState<boolean>(false);
  const [wizardMode, setWizardMode] = useState<boolean>(false);

  const createCampaignConfigurationFromData = (data: Campaign) => {
    const { configuration } = data;
    setID(data.id || "");
    setCampaign(configuration);
    setGifts(configuration.gifts ?? []);
    setActions(configuration.actions ?? []);
    setGame(configuration.game_type ?? "WHEEL");
    setPrimaryColor(configuration.colors?.primary ?? palette.primary.main);
    setSecondaryColor(configuration.colors?.secondary ?? palette.secondary.main);
    setLogo("");
    setLogoUri(configuration.logo_uri ?? "");
    setStartDate(configuration.start_date);
    setLabel(data.label ?? "");
    setPlaceId(data.placeId ?? "");
    setIsDisabled(configuration.disabled ?? false);
    setProfile(configuration.profile ?? "PREMIUM");
    setTheme(configuration.theme ?? "DEFAULT");
    setKioskMode(configuration.kiosk_mode ?? false);
    setWizardMode(!configuration.actions?.length && !configuration.gifts?.length);
    setSkipCooldown((data as any).skipCooldown ?? (configuration as any).skipCooldown ?? false);
    setVoucherStart((data as any).voucherStart ?? (configuration as any).voucherStart ?? 1);
    setVoucherExpiration((data as any).voucherExpiration ?? (configuration as any).voucherExpiration ?? null);
    setPromoCodes((data as any).promoCodes ?? []);
  };

  const saveCampaign = ({ wizardStep, forceSave }: { wizardStep?: number, forceSave?: boolean } = {}) => {
    return new Promise<Campaign | undefined>((resolve, reject) => {
      setIsSaving(true);
      
      if (!id) {
        snackbarShowMessage(t("errors.must-add-place"), "error");
        setIsSaving(false);
        return reject();
      }
      
      const steps = ["actions", "game", "customization", "gifts", "conditions", "date"];
      const currentGifts = getGiftsForSaveRef.current();

      if (!forceSave) {
        // always check actions, if wizard only check if... (?)
        let shouldCheck = (step: string) => wizardStep !== undefined ? wizardStep >= steps.indexOf(step) : true;

        if (!actions.length && !campaign?.is_proxy) {
          snackbarShowMessage(t("errors.actions.empty"), "error");
          setIsSaving(false);
          return reject();
        }
        if (actions.some(a => !isActionValid(a))) {
          snackbarShowMessage(t("errors.actions.target_missing"), "error");
          setIsSaving(false);
          return reject();
        }
        if (!campaign?.is_proxy) {
          if (!currentGifts.length && shouldCheck("gifts")) {
            snackbarShowMessage(t("errors.gifts.empty"), "error");
            setIsSaving(false);
            return reject();
          }
          if (
            currentGifts.some(
              (g) =>
                g.type !== "LOSS" &&
                (!g.name?.trim() ||
                  !g.type ||
                  (String(g.id ?? "").startsWith("new-") &&
                    (typeof g.limit !== "number" ||
                      Number.isNaN(g.limit) ||
                      (g.limit !== -1 &&
                        (g.initial_limit == null ||
                          Number.isNaN(g.initial_limit) ||
                          g.initial_limit < 0)))))
            ) &&
            shouldCheck("gifts")
          ) {
            snackbarShowMessage(t("errors.gifts.empty_fields"), "error");
            setIsSaving(false);
            return reject();
          }
          if (!currentGifts.some(g => g.type === "LOSS") && !currentGifts.some(g => g.limit && g.limit <= -1) && shouldCheck("gifts")) {
            snackbarShowMessage(t("errors.gifts.missing_unlimited"), "error");
            setIsSaving(false);
            return reject();
          }
          const hasPromoCodeGift = currentGifts.some((g) => g.type === GiftType.PROMO_CODE);
          const hasAtLeastOnePromoCode = promoCodes.some(
            (promoCode) => promoCode?.value && `${promoCode.value}`.trim().length > 0
          );
          if (hasPromoCodeGift && !hasAtLeastOnePromoCode && shouldCheck("gifts")) {
            snackbarShowMessage(t("errors.promoCodes.empty"), "error");
            setIsSaving(false);
            return reject();
          }
        }

        if (!startDate && shouldCheck("date") && wizardStep !== undefined) {
          snackbarShowMessage(t("errors.date.empty"), "error");
          setIsSaving(false);
          return reject();
        }
      }

      // Créer l'array des conditions global
      const conditions = conditionFields
        ?.filter(c => !c.isNoCondition && c.value && c.value.trim() !== "")
        .map(c => ({
          giftId: c.giftId,
          condition: c.value.trim()
        })) || [];
      const campaignData = {
        id,
        gifts: currentGifts.map((gift) => {
  
          const processedGift = {
            ...gift,
            ...(gift.id && !gift.id.startsWith("new") && { id: gift.id }),
            initial_limit: gift.initial_limit ?? gift.limit,
          };
          
          return processedGift;
        }),
        actions: actions.map(({ ...action }) => ({
          ...action,
        })),
        conditions: conditions,
        game_type: game,
        primaryColor,
        secondaryColor,
        logo_uri: logoUri,
        label,
        start_date: startDate,
        disabled: isDisabled,
        profile,
        theme,
        kiosk_mode: kioskMode,
        is_proxy: campaign?.is_proxy ?? false,
        targets: campaign?.targets ?? [],
        skipCooldown,
        voucherStart: voucherStart ?? 1,
        voucherExpiration: voucherExpiration ?? null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        promoCodes, // full list = source of truth; backend deletes codes absent from this array
      };
  
      HTTPClient.post(`/campaign/`, campaignData)
      .then(({ data }) => {
        resolve(data);
        snackbarShowMessage(t("success"));
      })
      .catch((err) => {
        // if 413 Content too large
        if (err.response?.status === 413) {
          snackbarShowMessage(t("errors.content_too_large"), "error");
          return;
        }
        if (err.response?.status === 403) {
          snackbarShowMessage(t("errors.forbidden"), "error");
          return;
        }
        reject();
        snackbarShowMessage(t("unknown-error"), "error");
      })
      .finally(() => {
        setIsSaving(false);
      });
    });
  };

  const fetchCampaign = () => {
    setIsLoaded(false);
    if (!selectedPlace) return;
    console.log("Fetching campaign: " + selectedPlace.id);
    HTTPClient.get("/campaign/" + selectedPlace.id)
      .then(({ data }: { data: Campaign }) => {
        createCampaignConfigurationFromData(data);
        setIsLoaded(true);
      })
      .catch(() => setIsLoaded(true));
  };

  useEffect(() => {
    console.log("Triggered fetchCampaign");
    fetchCampaign();
  }, [selectedPlace?.id]);

  const contextValue: CampaignContextType = {
    id,
    campaign,
    gifts,
    actions,
    game,
    primaryColor,
    secondaryColor,
    logo,
    logoUri,
    startDate,
    label,
    placeId,
    isLoaded,
    isSaving,
    isDisabled,
    profile,
    wizardMode,
    setID,
    setGifts,
    setActions,
    setGame,
    setPrimaryColor,
    setSecondaryColor,
    setLogo,
    setLogoUri,
    setStartDate,
    setLabel,
    setIsLoaded,
    setIsDisabled,
    setProfile,
    setCampaign,
    saveCampaign,
    registerGetGiftsForSave,
    conditionFields,
    setConditionFields,
    minPurchase,
    setMinPurchase,
    minPurchaseAmount,
    setMinPurchaseAmount,
    skipCooldown,
    setSkipCooldown,
    voucherExpiration,
    setVoucherExpiration,
    voucherStart,
    setVoucherStart,
    promoCodes,
    setPromoCodes,
    refresh: fetchCampaign,
  };

  return <CampaignContext.Provider value={contextValue}>{children}</CampaignContext.Provider>;
};

const useCampaign = () => {
  const context = useContext(CampaignContext);
  if (!context) {
    throw new Error("useCampaign must be used within a CampaignProvider");
  }
  return context;
};

export { CampaignProvider, useCampaign };
